import React, { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { createSlumSoccerContacts } from "../graphql/mutations"; // Adjust the import path as needed
import Swal from 'sweetalert2';

const client = generateClient();

export default function Contact(props) {
  const [formData, setFormData] = useState({
    name: "",
    contactNumber: "",
    country: "",
    city: "",
    email: "",
    enquiry: ""
  });

  // State for tracking touched fields
  const [touched, setTouched] = useState({
    name: false,
    contactNumber: false
  });

  // State for map iframe source
  const [mapSrc, setMapSrc] = useState(
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.899951749962!2d79.07072287439081!3d21.156379483384708!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c0f3c247d8ff%3A0xd4fe5420c2c18111!2sSlum%20Soccer!5e0!3m2!1sen!2sin!4v1738864786935!5m2!1sen!2sin"
  );

  // Map locations
  const locations = {
    nagpur: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3720.899951749962!2d79.07072287439081!3d21.156379483384708!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bd4c0f3c247d8ff%3A0xd4fe5420c2c18111!2sSlum%20Soccer!5e0!3m2!1sen!2sin!4v1738864786935!5m2!1sen!2sin",
    delhi: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d315.37542095403904!2d77.32297280008416!3d28.68896305754997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfbafb7688723%3A0x85b36c2240c39dc8!2sKaruna%20Hospital!5e0!3m2!1sen!2sin!4v1742884354260!5m2!1sen!2sin"
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    // Mark field as touched when user starts typing
    if (name === 'name' || name === 'contactNumber') {
      setTouched(prevTouched => ({
        ...prevTouched,
        [name]: true
      }));
    }
  };

  const handleShowMap = (location) => {
    // Update map source based on clicked location
    setMapSrc(locations[location]);
    window.scroll(0,0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if name and contact number are empty
    if (!formData.name || !formData.contactNumber) {
      // Show error notification for required fields
      Swal.fire({
        icon: 'error',
        title: 'Required Fields Missing',
        text: 'Name and Contact Number are required fields.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
      return; // Prevent form submission
    }

    try {
      // Prepare the input object for the mutation
      const input = {
        ...formData,
        date: new Date().toISOString(), // Add current date
        contactId: `contact_${Date.now()}` // Generate a unique ID
      };

      // Call the mutation to create a new contact
      const response = await client.graphql({
        query: createSlumSoccerContacts,
        variables: { input }
      });

      // Reset form after successful submission
      setFormData({
        name: "",
        contactNumber: "",
        country: "",
        city: "",
        email: "",
        enquiry: ""
      });

      // Reset touched state
      setTouched({
        name: false,
        contactNumber: false
      });

      // Show success sweet alert
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Your enquiry has been submitted successfully!',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      // Show error sweet alert
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'There was an error submitting your enquiry. Please try again.',
        confirmButtonColor: '#d33',
        confirmButtonText: 'Try Again'
      });
    }
  };

  return (
    <>
      <section className="text-center py-12">
        <h2 className="text-3xl text-orange-600 font-bold mb-4">
          Contact Information
        </h2>
        <iframe
          src={mapSrc}
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="mx-auto w-full md:w-[800px] lg:w-[1000px] rounded-lg shadow-lg"
        ></iframe>
        <div id="map"></div>
      </section>

      <h3 className="text-orange-600 p-40 text-3xl -mt-40 -mb-40 text-center">
        Get In Touch
      </h3>

      <div className="bg-white p-8 rounded-lg shadow-lg max-w-5xl mx-auto mt-12 mb-10">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Your name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg ${
                  touched.name && !formData.name 
                    ? 'border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              {touched.name && !formData.name && (
                <p className="text-red-500 text-sm mt-1">Name is required</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Contact number
              </label>
              <input
                type="text"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg ${
                  touched.contactNumber && !formData.contactNumber 
                    ? 'border-red-500' 
                    : 'border-gray-300'
                }`}
              />
              {touched.contactNumber && !formData.contactNumber && (
                <p className="text-red-500 text-sm mt-1">Contact Number is required</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Your country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Your city
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div className="mt-6">
            <label className="block text-sm font-medium mb-2">Email id</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="mt-6">
            <label className="block text-sm font-medium mb-2">Enquiry</label>
            <textarea
              name="enquiry"
              value={formData.enquiry}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-lg"
              rows="4"
            ></textarea>
          </div>
          <div className="mt-6">
            <button
              type="submit"
              className="w-full bg-orange-500 text-white p-2 rounded-lg hover:bg-blue-600"
            >
              Submit
            </button>
          </div>
        </form>
      </div>

      <h3 className="text-orange-600 p-40 text-3xl -mt-40 -mb-40 text-center">
        Our Office
      </h3>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        <div className="bg-white p-6 rounded-lg shadow-md mb-10">
          <h2 className="text-xl font-bold mb-4">Nagpur (Head Office)</h2>
          <p className="text-sm mb-2">Krida Vikas Sanstha Shantisadan,</p>
          <p className="text-sm mb-2">Lonara Road, Bokhara,</p>
          <p className="text-sm mb-4">Nagpur - 441111, Maharashtra, India.</p>
          <button 
            onClick={() => handleShowMap('nagpur')} 
            className="text-blue-500 hover:underline"
          >
            Show Map
          </button>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md mb-10">
          <h2 className="text-xl font-bold mb-4">Delhi Office</h2>
          <p className="text-sm mb-2">Monday to Saturday (9 am to 5 p.m)</p>
          <p className="text-sm mb-2">
            A116, Diishad Colony, Near Karuna Hospital,
          </p>
          <p className="text-sm mb-4">New Seemapuri, Delhi-95</p>
          <button 
            onClick={() => handleShowMap('delhi')} 
            className="text-blue-500 hover:underline"
          >
            Show Map
          </button>
        </div>
      </div>
    </>
  );
}